import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faSearch, faFilter } from '@fortawesome/free-solid-svg-icons';

const EmptyGlossaryState = ({ searchTerm, selectedFilter, theme }) => {
  const hasActiveFilters = searchTerm || selectedFilter !== 'ALL';

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      padding: '2rem',
    },
    card: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '3rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
      textAlign: 'center',
      maxWidth: '400px',
      width: '100%',
    },
    iconWrapper: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[100]}20, ${theme?.primary?.blue?.[200]}30)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
      fontSize: '2.5rem',
      color: theme?.primary?.blue?.[500],
      position: 'relative',
    },
    searchIcon: {
      position: 'absolute',
      bottom: '-0.5rem',
      right: '-0.5rem',
      fontSize: '1.5rem',
      background: theme?.background?.card,
      padding: '0.5rem',
      borderRadius: '50%',
      border: `2px solid ${theme?.borders?.primary}`,
      color: theme?.primary?.orange?.[500],
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '0.75rem',
    },
    message: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      lineHeight: 1.6,
      marginBottom: '1.5rem',
    },
    filterInfo: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1.5rem',
    },
    filterItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem',
      fontSize: '0.875rem',
    },
    filterIcon: {
      color: theme?.primary?.blue?.[500],
    },
    filterText: {
      color: theme?.text?.primary,
    },
    suggestion: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.container}>
      <motion.div
        style={styles.card}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          style={styles.iconWrapper}
          animate={{ 
            rotate: [0, -5, 5, 0],
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          <FontAwesomeIcon icon={faBook} />
          {hasActiveFilters && (
            <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
          )}
        </motion.div>
        
        <h2 style={styles.title}>
          {hasActiveFilters ? 'No Results Found' : 'No Glossary Terms'}
        </h2>
        
        {hasActiveFilters ? (
          <>
            <p style={styles.message}>
              We couldn't find any terms matching your current filters.
            </p>
            
            <div style={styles.filterInfo}>
              {searchTerm && (
                <div style={styles.filterItem}>
                  <FontAwesomeIcon icon={faSearch} style={styles.filterIcon} />
                  <span style={styles.filterText}>
                    Search: "{searchTerm}"
                  </span>
                </div>
              )}
              {selectedFilter !== 'ALL' && (
                <div style={{ ...styles.filterItem, marginBottom: 0 }}>
                  <FontAwesomeIcon icon={faFilter} style={styles.filterIcon} />
                  <span style={styles.filterText}>
                    Filter: {selectedFilter}
                  </span>
                </div>
              )}
            </div>
            
            <p style={styles.suggestion}>
              Try adjusting your search or removing filters to see more results.
            </p>
          </>
        ) : (
          <p style={styles.message}>
            No glossary terms are available for this certification yet. 
            Please check back later.
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default EmptyGlossaryState;